import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import { formatCurrency, formatCurrencyShort } from '../utils/dateUtils.ts';
import './InvestmentProjectionChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels,
  annotationPlugin
);

interface ChartPoint {
  age: number;
  nominal: number;
  real: number;
}

interface InvestmentProjectionChartProps {
  points: ChartPoint[];
  withdrawalStartAge?: number;
  strategyPoints?: Record<string, ChartPoint[]>;
  strategyWithdrawals?: Record<string, number>;
}

const InvestmentProjectionChart: React.FC<InvestmentProjectionChartProps> = ({
  points,
  withdrawalStartAge,
  strategyPoints,
  strategyWithdrawals,
}) => {
  if (!points || points.length === 0) {
    return (
      <div className="projection-chart empty">
        Nenhum dado de projeção disponível para o gráfico.
      </div>
    );
  }

  const displayIndices = points.reduce<number[]>((indices, _, index) => {
    if (index % 12 === 0 || index === points.length - 1) {
      indices.push(index);
    }
    return indices;
  }, []);
  const displayPoints = displayIndices.map((index) => points[index]);
  const lastIndex = displayPoints.length - 1;
  const withdrawalIndex =
    typeof withdrawalStartAge === 'number'
      ? displayPoints.reduce((closestIndex, point, index) => {
          const currentDiff = Math.abs(point.age - withdrawalStartAge);
          const closestDiff = Math.abs(displayPoints[closestIndex].age - withdrawalStartAge);
          return currentDiff < closestDiff ? index : closestIndex;
        }, 0)
      : null;

  const labels = displayPoints.map((point) => point.age.toFixed(0));
  const withdrawalLabel = withdrawalIndex !== null ? labels[withdrawalIndex] : null;
  const nominalValues = displayPoints.map((point) => point.nominal);
  const realValues = displayPoints.map((point) => point.real);
  const strategyDisplayMap = strategyPoints
    ? Object.entries(strategyPoints).reduce<Record<string, (number | null)[]>>((acc, [strategy, pointsData]) => {
        acc[strategy] = displayIndices.map((index) => {
          const point = pointsData?.[index];
          if (!point) return null;
          if (typeof withdrawalStartAge === 'number' && point.age < withdrawalStartAge) {
            return null;
          }
          return point.nominal;
        });
        return acc;
      }, {})
    : null;
  const strategyConfig = [
    { key: 'sustainable', label: 'Sustentável', color: '#6B7280' },
    { key: 'partial', label: 'Planejada', color: '#F59E0B' },
    { key: 'full', label: 'Total', color: '#EF4444' },
  ];
  const strategyDatasets = strategyDisplayMap
    ? strategyConfig
        .filter((strategy) => strategyDisplayMap[strategy.key]?.some((value) => value !== null))
        .map((strategy) => ({
          label: `Retirada ${strategy.label}`,
          data: strategyDisplayMap[strategy.key],
          borderColor: strategy.color,
          borderWidth: 2,
          borderDash: [6, 6],
          pointRadius: 0,
          tension: 0.25,
          fill: false,
          spanGaps: false,
          datalabels: {
            display: false,
          },
        }))
    : [];

  const data = {
    labels,
    datasets: [
      {
        label: 'Patrimônio nominal',
        data: nominalValues,
        borderColor: '#004AAD',
        backgroundColor: (context: any) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return 'rgba(0, 74, 173, 0.1)';
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(0, 74, 173, 0.35)');
          gradient.addColorStop(1, 'rgba(0, 74, 173, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.25,
        borderWidth: 0,
        pointRadius: 0,
        pointHitRadius: 6,
      },
      {
        label: 'Patrimônio real',
        data: realValues,
        borderColor: '#00B894',
        backgroundColor: 'rgba(0, 184, 148, 0.15)',
        fill: false,
        tension: 0.25,
        borderWidth: 2,
        pointRadius: 0,
      },
      ...strategyDatasets,
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${formatCurrencyShort(context.parsed.y)}`,
        },
      },
      datalabels: {
        display: (context: any) => {
          const dataIndex = context.dataIndex;
          if (context.datasetIndex > 1) {
            return false;
          }
          return dataIndex === 0 || dataIndex === lastIndex || dataIndex === withdrawalIndex;
        },
        formatter: (value: any) => formatCurrencyShort(Number(value)),
        color: (context: any) => context.dataset.borderColor,
        align: (context: any) => (context.datasetIndex === 0 ? 'top' : 'bottom'),
        anchor: 'end',
        offset: 6,
        clamp: true,
        clip: false,
      },
      annotation: withdrawalLabel
        ? {
            annotations: {
              withdrawalLine: {
                type: 'line',
                xMin: withdrawalLabel,
                xMax: withdrawalLabel,
                borderColor: 'rgba(107, 114, 128, 0.8)',
                borderWidth: 1,
                borderDash: [6, 6],
                label: {
                  display: false,
                },
              },
            },
          }
        : undefined,
    },
    layout: {
      padding: {
        right: 24,
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        ticks: {
          callback: (value: any) => formatCurrencyShort(Number(value)),
        },
      },
    },
  };

  return (
    <div className="projection-chart">
      <div className="chart-wrapper">
        <Line data={data} options={options} />
      </div>
      {strategyWithdrawals && (
        <div className="strategy-withdrawals">
          <h4>Retirada mensal inicial por estratégia</h4>
          <div className="strategy-withdrawals-list">
            {strategyConfig.map((strategy) => {
              const value = strategyWithdrawals[strategy.key] ?? 0;
              return (
                <div key={strategy.key} className="strategy-withdrawals-item">
                  <span className="strategy-label" style={{ color: strategy.color }}>
                    {strategy.label}
                  </span>
                  <span className="strategy-value">
                    {value > 0 ? formatCurrency(value) : 'Sem retirada'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentProjectionChart;
